import React, { useState, useEffect } from 'react';
import StatCard from '../components/dashboard/StatCard';
import RevenueChart from '../components/dashboard/RevenueChart';
import ActivityFeed from '../components/dashboard/ActivityFeed';
import { Building2, ShieldCheck, Users, MessageSquare, Plus } from 'lucide-react';
import { companyApi } from '../api/companyApi';
import { adminApi } from '../api/adminApi';
import { employeeApi } from '../api/employeeApi';
import { chatApi } from '../api/chatApi';
import { useSocket } from '../hooks/useSocket';
import { useNavigate } from 'react-router-dom';

const Dashboard = () => {
  const [stats, setStats] = useState({
    companies: 0,
    admins: 0,
    employees: 0,
    activeChats: 0
  });
  const [loading, setLoading] = useState(true);
  const { socket } = useSocket();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchDashboardStats = async () => {
      try {
        const [compRes, adminRes, empRes, chatRes] = await Promise.all([
          companyApi.getCompanies({ limit: 1 }),
          adminApi.getAdmins({ limit: 1 }),
          employeeApi.getEmployees({ limit: 1 }),
          chatApi.getChatAdmins().catch(() => ({ data: { admins: [] } }))
        ]);

        setStats({
          companies: compRes.data.pagination.total || 0,
          admins: adminRes.data.pagination.total || 0,
          employees: empRes.data.pagination.total || 0,
          activeChats: chatRes.data.admins?.length || 0
        });
      } catch (err) {
        console.error('Failed to fetch dashboard stats:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardStats();
  }, []);

  return (
    <div className="flex flex-col gap-6 w-full pb-8">

      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-slate-900 tracking-tight font-display">Overview</h1>
          <p className="text-xs text-slate-500 mt-0.5">Real-time multi-tenant monitoring and company operations.</p>
        </div>
        <button
          onClick={() => navigate('/companies')}
          className="flex items-center gap-2 px-4 py-2 rounded-xl bg-green-700 hover:bg-green-800 text-white font-semibold text-sm shadow-sm transition-all hover:-translate-y-0.5"
        >
          <Plus className="w-4 h-4" />
          <span>Onboard Company</span>
        </button>
      </div>

      {/* Stat Cards — real API data */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard
          title="Total Companies"
          value={loading ? '...' : stats.companies}
          icon={Building2}
          color="green"
        />
        <StatCard
          title="Total Admins"
          value={loading ? '...' : stats.admins}
          icon={ShieldCheck}
          color="emerald"
        />
        <StatCard
          title="Managed Employees"
          value={loading ? '...' : stats.employees}
          icon={Users}
          color="teal"
        />
        <StatCard
          title="Active Admin Chats"
          value={loading ? '...' : stats.activeChats}
          icon={MessageSquare}
          isLive={true}
          color="green"
        />
      </div>

      {/* Chart + Activity Feed */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2 h-[420px] min-h-0 overflow-hidden rounded-2xl bg-white border border-slate-100 shadow-sm flex flex-col">
          <RevenueChart />
        </div>
        <div className="h-[420px] min-h-0 overflow-hidden rounded-2xl bg-white border border-slate-100 shadow-sm flex flex-col">
          <ActivityFeed socket={socket} />
        </div>
      </div>

    </div>
  );
};

export default Dashboard;
